<template>
  <div class="input-phone-wrapper">
    <!-- Label -->
    <label v-if="label" :for="elemId" class="input-label">
      <i v-if="isRequired && !hasValidation" class="pi pi-exclamation-triangle text-warning"></i>
      <i v-if="isRequired && hasValidation" class="pi pi-check text-success"></i>
      <span v-if="isRequired" class="required-asterisk">*</span>
      <span>{{ label }}</span>
    </label>

    <!-- Phone input with country selector -->
    <div class="phone-input-container">
      <!-- Country Selector -->
      <Select
        v-model="selectedCountry"
        :options="countries"
        optionLabel="name"
        optionValue="code"
        :placeholder="t('País')"
        :disabled="isDisabled"
        class="country-select"
        :data-testid="`${elemId}-country-select`"
      >
        <template #value="slotProps">
          <div v-if="slotProps.value" class="country-option">
            <span class="country-flag">{{ getCountryFlag(slotProps.value) }}</span>
            <span class="country-code">{{ getCountryDialCode(slotProps.value) }}</span>
          </div>
          <span v-else>{{ slotProps.placeholder }}</span>
        </template>
        <template #option="slotProps">
          <div class="country-option">
            <span class="country-flag">{{ getCountryFlag(slotProps.option.code) }}</span>
            <span class="country-name">{{ slotProps.option.name }}</span>
            <span class="country-code">{{ slotProps.option.dialCode }}</span>
          </div>
        </template>
      </Select>

      <!-- Phone Number Input -->
      <InputMask
        v-model="phoneValue"
        :id="elemId"
        :mask="currentMask"
        :placeholder="currentPlaceholder"
        :disabled="isDisabled"
        :invalid="isRequired && !hasValidation"
        fluid
        class="phone-input"
        @update:modelValue="handleValueChange"
        @input="markAsTyping"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import InputMask from 'primevue/inputmask';
import Select from 'primevue/select';
import { useI18n } from 'vue-i18n';
import { log } from '../utils/DebugLogger';

const COMPONENT_NAME = 'InputPhone';

const { t, locale } = useI18n();

interface PhoneObject {
  countryCode: string;
  areaCode: string;
  phoneNumber: string;
}

interface CountryDef {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
  mask: string;
  mask8?: string;
  placeholder: string;
}

interface CountryConfig {
  code: string;
  countryCode: string;
  areaCodeLength: number;
}

const props = defineProps({
  elemId: String,
  placeholder: String,
  label: String,
  validation: {
    type: Function as import('vue').PropType<() => boolean>,
    default: () => true
  },
  ngRequired: {
    type: [String, Boolean],
    default: false
  },
  disabled: {
    type: [String, Boolean],
    default: false
  },
  // v-model binding - accepts both Object and String formats
  // Object: { countryCode: '+55', areaCode: '47', phoneNumber: '997694646' }
  // String: '+5547997694646' (E.164 format)
  modelValue: {
    type: [Object, String] as import('vue').PropType<PhoneObject | string>,
    default: () => ({ countryCode: '', areaCode: '', phoneNumber: '' })
  },
  defaultCountry: {
    type: String,
    default: 'br'
  },
  language: {
    type: String,
    default: 'PT'
  }
});

const emit = defineEmits<{
  'update:modelValue': [value: PhoneObject];
}>();

// Country definitions
const countries = ref([
  { code: 'br', name: 'Brasil', dialCode: '+55', flag: '🇧🇷', mask: '(99)99999-9999', placeholder: '(11)98765-4321' },
  { code: 'cl', name: 'Chile', dialCode: '+56', flag: '🇨🇱', mask: '9 9999 9999', placeholder: '9 1234 5678' },
  { code: 'us', name: 'United States', dialCode: '+1', flag: '🇺🇸', mask: '(999) 999-9999', placeholder: '(555) 123-4567' },
  { code: 'gb', name: 'United Kingdom', dialCode: '+44', flag: '🇬🇧', mask: '9999 999999', placeholder: '7700 123456' },
  { code: 'fr', name: 'France', dialCode: '+33', flag: '🇫🇷', mask: '9 99 99 99 99', placeholder: '6 12 34 56 78' },
  { code: 'de', name: 'Deutschland', dialCode: '+49', flag: '🇩🇪', mask: '999 99999999', placeholder: '151 12345678' },
  { code: 'it', name: 'Italia', dialCode: '+39', flag: '🇮🇹', mask: '999 9999999', placeholder: '320 1234567' },
  { code: 'es', name: 'España', dialCode: '+34', flag: '🇪🇸', mask: '999 999 999', placeholder: '612 345 678' },
  { code: 'pt', name: 'Portugal', dialCode: '+351', flag: '🇵🇹', mask: '999 999 999', placeholder: '912 345 678' },
  { code: 'mx', name: 'México', dialCode: '+52', flag: '🇲🇽', mask: '999 999 9999', placeholder: '55 1234 5678' },
  { code: 'ar', name: 'Argentina', dialCode: '+54', flag: '🇦🇷', mask: '999 9999-9999', placeholder: '11 1234-5678' },
  { code: 'bo', name: 'Bolivia', dialCode: '+591', flag: '🇧🇴', mask: '9999 9999', placeholder: '7 1234567' },
  { code: 'ec', name: 'Ecuador', dialCode: '+593', flag: '🇪🇨', mask: '99 999 9999', placeholder: '99 123 4567' },
  { code: 'gt', name: 'Guatemala', dialCode: '+502', flag: '🇬🇹', mask: '9999 9999', placeholder: '1234 5678' },
  { code: 'py', name: 'Paraguay', dialCode: '+595', flag: '🇵🇾', mask: '999 999999', placeholder: '21 123456' },
  { code: 'uy', name: 'Uruguay', dialCode: '+598', flag: '🇺🇾', mask: '99 999 999', placeholder: '94 123 456' },
  { code: 'ao', name: 'Angola', dialCode: '+244', flag: '🇦🇴', mask: '999 999 999', placeholder: '923 123 456' }
]);

// Known country codes for parsing E.164 strings (ordered by code length, longest first)
const countryConfigs = [
  { code: '+351', countryCode: 'pt', areaCodeLength: 0 }, // Portugal
  { code: '+55', countryCode: 'br', areaCodeLength: 2 },  // Brazil
  { code: '+56', countryCode: 'cl', areaCodeLength: 0 },  // Chile
  { code: '+52', countryCode: 'mx', areaCodeLength: 0 },  // Mexico
  { code: '+54', countryCode: 'ar', areaCodeLength: 0 },  // Argentina
  { code: '+49', countryCode: 'de', areaCodeLength: 0 },  // Germany
  { code: '+44', countryCode: 'gb', areaCodeLength: 0 },  // UK
  { code: '+39', countryCode: 'it', areaCodeLength: 0 },  // Italy
  { code: '+34', countryCode: 'es', areaCodeLength: 0 },  // Spain
  { code: '+33', countryCode: 'fr', areaCodeLength: 0 },  // France
  { code: '+1', countryCode: 'us', areaCodeLength: 3 },   // US/Canada
  { code: '+502', countryCode: 'gt', areaCodeLength: 0 }, // Guatemala
  { code: '+591', countryCode: 'bo', areaCodeLength: 0 }, // Bolivia
  { code: '+593', countryCode: 'ec', areaCodeLength: 0 }, // Ecuador
  { code: '+595', countryCode: 'py', areaCodeLength: 0 }, // Paraguay
  { code: '+598', countryCode: 'uy', areaCodeLength: 0 }, // Uruguay
  { code: '+244', countryCode: 'ao', areaCodeLength: 0 }, // Angola
];

/**
 * Parse phone data from various input formats
 * Handles: Object { countryCode, areaCode, phoneNumber } and String "+5547997694646"
 * @param {Object|String} data - Input data
 * @returns {Object} { countryCode: 'br', fullPhone: '47997694646' }
 */
const parsePhoneData = (data: PhoneObject | string | null | undefined): { countryCode: string; fullPhone: string } => {
  log(COMPONENT_NAME, 'parsePhoneData', 'Parsing:', data);

  const result = { countryCode: props.defaultCountry, fullPhone: '' };

  if (!data) {
    return result;
  }

  // Handle Object format: { countryCode: '+55', areaCode: '47', phoneNumber: '997694646' }
  if (typeof data === 'object') {
    // Find country by dial code
    if (data.countryCode) {
      const foundCountry = countries.value.find(c => c.dialCode === data.countryCode);
      if (foundCountry) {
        result.countryCode = foundCountry.code;
      }
    }

    // Combine areaCode and phoneNumber for display
    const areaCode = data.areaCode || '';
    const phoneNumber = data.phoneNumber || '';
    result.fullPhone = `${areaCode}${phoneNumber}`.replace(/\D/g, '');

    log(COMPONENT_NAME, 'parsePhoneData', 'Parsed object:', result);
    return result;
  }

  // Handle String format: "+5547997694646" (E.164)
  if (typeof data === 'string' && data.trim() !== '') {
    const cleanString = data.trim();

    // Try to detect country by prefix
    if (cleanString.startsWith('+')) {
      for (const config of countryConfigs) {
        if (cleanString.startsWith(config.code)) {
          result.countryCode = config.countryCode;
          result.fullPhone = cleanString.substring(config.code.length);
          log(COMPONENT_NAME, 'parsePhoneData', 'Parsed E.164 string:', result);
          return result;
        }
      }
    }

    // No country code prefix, just use digits
    result.fullPhone = cleanString.replace(/\D/g, '');
    log(COMPONENT_NAME, 'parsePhoneData', 'Parsed plain string:', result);
    return result;
  }

  return result;
};

// State
const selectedCountry = ref<string>(props.defaultCountry);
const phoneValue = ref<string>('');
const isInitializing = ref<boolean>(true);
const isTyping = ref<boolean>(false);
const lockedMask = ref<string | null>(null); // Lock mask during typing to prevent reformatting
let typingTimeout: ReturnType<typeof setTimeout> | null = null;

// Computed
const isRequired = computed(() => props.ngRequired === true || props.ngRequired === 'true');
const isDisabled = computed(() => props.disabled === true || props.disabled === 'true');
const hasValidation = computed(() => props.validation());

const currentCountry = computed(() =>
  countries.value.find(c => c.code === selectedCountry.value) || countries.value[0]
);

const currentMask = computed(() => {
  // If mask is locked during typing, use the locked mask
  if (lockedMask.value) {
    return lockedMask.value;
  }

  const country = currentCountry.value;

  return country.mask;
});

const currentPlaceholder = computed(() => props.placeholder || currentCountry.value.placeholder);

// Helper functions
const getCountryFlag = (code: string): string => {
  const country = countries.value.find(c => c.code === code);
  return country ? country.flag : '🌍';
};

const getCountryDialCode = (code: string): string => {
  const country = countries.value.find(c => c.code === code);
  return country ? country.dialCode : '';
};

// Extract digits only
const getDigits = (value: string): string => (value || '').replace(/\D/g, '');

// Split phone value into parts based on country
const splitPhoneValue = (digits: string, countryCode: string): { areaCode: string; phoneNumber: string } => {
  if (!digits) return { areaCode: '', phoneNumber: '' };

  if (countryCode === 'br') {
    return {
      areaCode: digits.substring(0, 2),
      phoneNumber: digits.substring(2)
    };
  } else if (countryCode === 'us') {
    return {
      areaCode: digits.substring(0, 3),
      phoneNumber: digits.substring(3)
    };
  } else {
    // For other countries, no area code separation
    return {
      areaCode: '',
      phoneNumber: digits
    };
  }
};

// Combine parts into display value
const combinePhoneValue = (areaCode: string, phoneNumber: string): string => {
  return (areaCode + phoneNumber).replace(/\D/g, '');
};

// Event handlers
//
// Split into two intents (was: one `handleInput` that did both, which caused
// programmatic value changes — mount-time parse, mask formatting, parent
// prop updates — to falsely flip isTyping=true and then drop the next
// legitimate watch(modelValue) update for 500ms).
//
//   markAsTyping  → bound to @input on InputMask (only fires from real DOM
//                   input events / keystrokes). Sets isTyping + locks the
//                   mask to prevent BR 8↔9-digit mask switching mid-typing.
//   handleValueChange → bound to @update:modelValue (fires for any value
//                   change including programmatic). Just re-emits the parsed
//                   PhoneObject upstream. Does NOT touch isTyping.

const markAsTyping = (): void => {
  // Lock the current mask so it can't switch underneath the user mid-edit
  if (!lockedMask.value) {
    lockedMask.value = currentMask.value;
    log(COMPONENT_NAME, 'markAsTyping', 'Locked mask:', lockedMask.value);
  }
  isTyping.value = true;
  if (typingTimeout) clearTimeout(typingTimeout);
  typingTimeout = setTimeout(() => {
    isTyping.value = false;
    lockedMask.value = null;
    log(COMPONENT_NAME, 'markAsTyping', 'Unlocked mask');
  }, 500);
};

const handleValueChange = (): void => {
  if (isInitializing.value) return;

  const digits = getDigits(phoneValue.value);
  const parts = splitPhoneValue(digits, selectedCountry.value);
  const country = currentCountry.value;

  log(COMPONENT_NAME, 'handleValueChange', 'Emitting:', {
    countryCode: country.dialCode,
    areaCode: parts.areaCode,
    phoneNumber: parts.phoneNumber
  });

  emit('update:modelValue', {
    countryCode: country.dialCode,
    areaCode: parts.areaCode,
    phoneNumber: parts.phoneNumber
  });
};

// Watch for external changes to modelValue (handles both Object and String formats)
watch(() => props.modelValue, (newValue, oldValue) => {
  // Skip if user is actively typing to prevent input reset
  if (isTyping.value) {
    log(COMPONENT_NAME, 'watch:modelValue', 'Skipping - user is typing');
    return;
  }

  // Skip during initialization
  if (isInitializing.value) {
    log(COMPONENT_NAME, 'watch:modelValue', 'Skipping - initializing');
    return;
  }

  log(COMPONENT_NAME, 'watch:modelValue', 'Received:', newValue, 'Previous:', oldValue);

  // Handle null/undefined/empty - might be a form reset
  if (!newValue || (typeof newValue === 'object' && !newValue.phoneNumber && !newValue.areaCode)) {
    // Check if this is a real reset (had value before)
    const currentDigits = getDigits(phoneValue.value);
    if (currentDigits.length > 0) {
      log(COMPONENT_NAME, 'watch:modelValue', 'Form reset detected, clearing');
      phoneValue.value = '';
      selectedCountry.value = props.defaultCountry;
    }
    return;
  }

  // Parse the input (handles both String and Object)
  const parsed = parsePhoneData(newValue);
  const currentDigits = getDigits(phoneValue.value);

  // Only update if there's a meaningful change
  if (parsed.fullPhone !== currentDigits) {
    log(COMPONENT_NAME, 'watch:modelValue', 'Updating from parsed:', parsed);
    selectedCountry.value = parsed.countryCode;
    phoneValue.value = parsed.fullPhone;
  } else if (parsed.countryCode !== selectedCountry.value) {
    // Country changed but phone same
    log(COMPONENT_NAME, 'watch:modelValue', 'Updating country only:', parsed.countryCode);
    selectedCountry.value = parsed.countryCode;
  }
}, { deep: true });

// Watch for country changes
watch(selectedCountry, () => {
  if (isInitializing.value) return;
  handleValueChange();
});

// Follow parent-driven default-country changes (e.g. a Chilean franchise is
// selected after mount, so defaultCountry switches 'br' -> 'cl'). selectedCountry
// is only seeded from defaultCountry once at init, so without this the country
// selector + mask stay on the old country. Only switch while the user has NOT
// entered a number yet, so we never clobber a number they are typing/have typed.
watch(() => props.defaultCountry, (newCountry) => {
  if (!newCountry || phoneValue.value || newCountry === selectedCountry.value) return;
  selectedCountry.value = newCountry;
});

// Exposed for integration tests to assert the active country reacts to the
// franchise-driven defaultCountry (see cliente-chile-phone integration spec).
defineExpose({ selectedCountry });

// Initialize from props (handles both Object and String formats)
onMounted(async () => {
  log(COMPONENT_NAME, 'mounted', 'Props:', props.modelValue);

  if (props.modelValue) {
    // Parse the initial value (handles both String and Object)
    const parsed = parsePhoneData(props.modelValue);

    selectedCountry.value = parsed.countryCode;
    phoneValue.value = parsed.fullPhone;

    log(COMPONENT_NAME, 'mounted', 'Initialized:', {
      country: selectedCountry.value,
      phoneValue: phoneValue.value
    });
  }

  // Allow updates after initialization with nextTick for proper Vue reactivity
  await nextTick();
  isInitializing.value = false;

  // Only emit if we have actual phone data — use handleValueChange (no
  // typing flag) so the mount-time round-trip doesn't suppress the next
  // legitimate parent prop update.
  if (phoneValue.value) {
    handleValueChange();
  }
});

// Cleanup
onUnmounted(() => {
  if (typingTimeout) {
    clearTimeout(typingTimeout);
  }
  lockedMask.value = null;
});
</script>

<style scoped>
.input-phone-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  margin-bottom: 1rem;
}

.input-label {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: 500;
  color: var(--p-text-color);
}

.required-asterisk {
  color: var(--p-red-500);
  margin-right: 0.25rem;
}

.text-warning {
  color: var(--p-yellow-500);
}

.text-success {
  color: var(--p-green-500);
}

.phone-input-container {
  display: flex;
  gap: 0.5rem;
  width: 100%;
}

.country-select {
  flex: 0 0 140px;
  min-width: 140px;
}

.phone-input {
  flex: 1;
  min-width: 0;
}

.country-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.country-flag {
  font-size: 1.2rem;
  line-height: 1;
}

.country-name {
  flex: 1;
}

.country-code {
  color: var(--p-text-muted-color);
  font-size: 0.875rem;
  font-weight: 500;
}

@media (max-width: 768px) {
  .phone-input-container {
    flex-direction: column;
  }

  .country-select {
    flex: 1;
    width: 100%;
  }
}
</style>
